import React, { Component } from 'react';
import { Form, Input, Button } from 'antd';
import { reverse } from 'named-urls';
import keyMirror from 'keymirror';
import { withApollo } from '@apollo/react-hoc';
import { gql } from '@apollo/client';
import routes from '@achievable-me/tundra-wizard/routes';
import ProductLayout from '$components/layout/ProductLayout';
import Seo from '$components/Seo';

import styles from './styles.scss';
import { track } from '$lib/tracking';

const CONTACT_US = gql`
  mutation ContactUs($input: ContactUsInput!) {
    contactUs(input: $input) {
      _empty
    }
  }
`;

const FEEDBACK_STATE = keyMirror({
  ready: null,
  sending: null,
  sent: null,
});

class Feedback extends Component {
  constructor() {
    super();
    this.state = {
      feedbackState: FEEDBACK_STATE.ready,
    };
    track('Feedback | Viewed');
  }

  onFinish = async (values) => {
    this.setState({
      feedbackState: FEEDBACK_STATE.sending,
    });

    const { client } = this.props;
    const { email, message } = values;

    try {
      await client.mutate({
        mutation: CONTACT_US,
        variables: {
          input: {
            email,
            message,
          },
        },
      });
    } catch (feedbackError) {
      this.setState({
        feedbackState: FEEDBACK_STATE.ready,
      });
      return;
    }

    this.setState({
      feedbackState: FEEDBACK_STATE.sent,
    });

    track('Feedback | Sent');
  };

  render() {
    const { currentUser, location, match } = this.props;
    const { feedbackState } = this.state;

    const loading = feedbackState === FEEDBACK_STATE.sending;
    const disabled = feedbackState !== FEEDBACK_STATE.ready;

    const buttonText = (() => {
      if (feedbackState === FEEDBACK_STATE.sending) {
        return 'Sending...';
      }

      if (feedbackState === FEEDBACK_STATE.sent) {
        return 'Sent!';
      }

      return 'Send feedback';
    })();

    return (
      <ProductLayout currentUser={currentUser} location={location} match={match}>
        {() => (
          <div className={styles.layout}>
            <div className={styles.well}>
              <Seo
                title="Feedback"
                canonical={reverse(routes.study.feedback, { productSlug: 'achievable' })}
                description="At Achievable, user feedback drives product decisions. Let us know your thoughts!"
              />
              <Form onFinish={this.onFinish} className={styles.form}>
                <h1 className={styles.header}>Feedback</h1>
                <div className={styles.subheader}>
                  At Achievable, user feedback drives product decisions.
                </div>
                <div className={styles.description}>
                  <p>
                    We want to hear from you! Is there anything you feel like we could
                    improve? Anything we&apos;re doing really well? Have an idea for a
                    great new feature, or a tiny quality of life improvement? Just want to
                    say hi? The form below is a direct line to our inbox - let us know how
                    you feel. We aim to respond to all comments by the next business day,
                    and we&apos;re usually much quicker than that. Our small startup team
                    is based in San Francisco and we live and breathe Achievable, so you
                    may see our responses come at odd hours as we work around the clock.
                  </p>
                  <p>
                    You can also post in our{' '}
                    <a
                      href="https://talk.achievable.me/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fad fa-comments" /> community forum{' '}
                      <i className="fad fa-external-link" />
                    </a>{' '}
                    or call/text us at{' '}
                    <a href="tel:415-831-7733">
                      <i className="fad fa-phone" />
                      415-831-7733
                    </a>
                    .
                  </p>
                </div>
                <Form.Item
                  name="message"
                  rules={[{ required: true, message: 'Please enter a message' }]}
                >
                  <Input.TextArea
                    disabled={disabled}
                    className={styles.textarea}
                    placeholder="Let us know your thoughts!"
                  />
                </Form.Item>
                {!currentUser && (
                  <p>
                    <b>Include your email if you want a reply back!</b>
                  </p>
                )}
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={disabled}
                  >
                    {buttonText}
                  </Button>
                </Form.Item>
                {feedbackState === FEEDBACK_STATE.sent && (
                  <div>
                    Your feedback has been sent - we&apos;ll get back to you soon!
                  </div>
                )}
              </Form>
            </div>
          </div>
        )}
      </ProductLayout>
    );
  }
}

export default withApollo(Feedback);
